import type { GameState, GameConfig, Square, Piece, Player, Facing, PieceType, Pickup } from './types'
import { generateInitialEvents, EVENT_CADENCE } from './events'

// ── Pickup utilities ───────────────────────────────────────────────────────────

const TRANSFORM_TARGETS: Array<Exclude<PieceType, 'king'>> = ['queen', 'rook', 'bishop', 'knight', 'pawn']

function randomPickup(spawnedOnTurn: number): Pickup {
  if (Math.random() < 0.5) return { kind: 'upgrade', spawnedOnTurn }
  const to = TRANSFORM_TARGETS[Math.floor(Math.random() * TRANSFORM_TARGETS.length)]
  return { kind: 'transform', to, spawnedOnTurn }
}

/**
 * Spawn one pickup on a random empty, pickup-free square.
 * Returns state unchanged if no valid square is available.
 */
export function spawnPickup(state: GameState): GameState {
  const candidates = Object.keys(state.squares).filter(
    key => !state.squares[key].piece && !state.pickups[key]
  )
  if (candidates.length === 0) return state
  const key = candidates[Math.floor(Math.random() * candidates.length)]
  return { ...state, pickups: { ...state.pickups, [key]: randomPickup(state.turnNumber) } }
}

/**
 * Assign each square to the "zone" of the nearest player home edge (Voronoi by distance).
 * Returns a map of squareKey → player index.
 */
function getPlayerZones(state: GameState): Record<string, number> {
  const boardMax = state.boardSize - 1
  const alivePlayers = state.players.filter(p => p.alive)
  const zones: Record<string, number> = {}

  for (const key of Object.keys(state.squares)) {
    const [col, row] = key.split(',').map(Number)
    let minDist = Infinity
    let zone = alivePlayers[0]?.index ?? 0
    for (const player of alivePlayers) {
      let dist: number
      switch (player.facing) {
        case 'UP':    dist = boardMax - row; break
        case 'DOWN':  dist = row; break
        case 'RIGHT': dist = col; break
        case 'LEFT':  dist = boardMax - col; break
        default:      dist = Infinity
      }
      if (dist < minDist) { minDist = dist; zone = player.index }
    }
    zones[key] = zone
  }
  return zones
}

/**
 * Spawn one pickup in the player zone that currently has the fewest pickups.
 * Falls back to random if no empty squares are available in any underfilled zone.
 */
export function spawnPickupBalanced(state: GameState): GameState {
  const zones = getPlayerZones(state)

  // Count existing pickups per zone
  const counts: Record<number, number> = {}
  for (const player of state.players) {
    if (player.alive) counts[player.index] = 0
  }
  for (const key of Object.keys(state.pickups)) {
    const z = zones[key]
    if (z !== undefined && counts[z] !== undefined) counts[z]++
  }

  // Find zone(s) with the minimum count
  const minCount = Math.min(...Object.values(counts))
  const targetZones = new Set(
    Object.entries(counts).filter(([, c]) => c === minCount).map(([i]) => Number(i))
  )

  // Candidates: empty pickup-free squares in an underfilled zone
  const candidates = Object.keys(state.squares).filter(
    key => !state.squares[key].piece && !state.pickups[key] && targetZones.has(zones[key])
  )

  if (candidates.length === 0) return spawnPickup(state)
  const key = candidates[Math.floor(Math.random() * candidates.length)]
  return { ...state, pickups: { ...state.pickups, [key]: randomPickup(state.turnNumber) } }
}

/**
 * Check if a coordinate is valid on the given board
 */
export function isValidSquare(col: number, row: number, boardSize: 8 | 14): boolean {
  // Bounds check
  if (col < 0 || row < 0 || col >= boardSize || row >= boardSize) {
    return false
  }

  // For 8x8, all squares are valid
  if (boardSize === 8) {
    return true
  }

  // For 14x14, exclude 3x3 corner cutouts
  const isCutCorner =
    (col <= 2 && row <= 2) ||        // top-left
    (col >= 11 && row <= 2) ||       // top-right
    (col <= 2 && row >= 11) ||       // bottom-left
    (col >= 11 && row >= 11)         // bottom-right

  return !isCutCorner
}

/**
 * Generate the set of all valid squares for a board
 */
function generateValidSquares(boardSize: 8 | 14): Record<string, Square> {
  const squares: Record<string, Square> = {}

  for (let col = 0; col < boardSize; col++) {
    for (let row = 0; row < boardSize; row++) {
      if (isValidSquare(col, row, boardSize)) {
        const key = `${col},${row}`
        squares[key] = { col, row, piece: null }
      }
    }
  }

  return squares
}

/**
 * Create players for a game
 */
function createPlayers(playerCount: number, botSlots?: boolean[]): Player[] {
  const players: Player[] = []
  const colors = ['#ffffff', '#ff4444', '#4444ff', '#44ff44'] // white, red, blue, green
  const names = ['White', 'Red', 'Blue', 'Green']
  const facings: Facing[] = ['UP', 'DOWN', 'RIGHT', 'LEFT'] // bottom, top, left, right

  for (let i = 0; i < playerCount; i++) {
    players.push({
      index: i,
      name: names[i],
      color: colors[i],
      facing: facings[i],
      alive: true,
      inCheck: false,
      enPassantTarget: null,
      castlingRights: { kingSide: true, queenSide: true },
      controlledBy: botSlots?.[i] ? 'bot' : 'human',
    })
  }

  return players
}

/**
 * Get starting positions for pieces based on board size and player count
 */
function getStartingPositions(boardSize: 8 | 14, playerCount: number): Array<{col: number, row: number, piece: Piece}> {
  const positions: Array<{col: number, row: number, piece: Piece}> = []

  if (boardSize === 8) {
    // 2-player mode on 8x8 board
    const pieceOrder: PieceType[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook']

    // Player 0 (White) - bottom two rows
    for (let col = 0; col < 8; col++) {
      // Pawns on row 6
      positions.push({
        col, row: 6,
        piece: { type: 'pawn', owner: 0, facing: 'UP', hasMoved: false }
      })
      // Back rank on row 7
      positions.push({
        col, row: 7,
        piece: { type: pieceOrder[col], owner: 0, facing: 'UP', hasMoved: false }
      })
    }

    // Player 1 (Black) - top two rows
    for (let col = 0; col < 8; col++) {
      // Pawns on row 1
      positions.push({
        col, row: 1,
        piece: { type: 'pawn', owner: 1, facing: 'DOWN', hasMoved: false }
      })
      // Back rank on row 0
      positions.push({
        col, row: 0,
        piece: { type: pieceOrder[col], owner: 1, facing: 'DOWN', hasMoved: false }
      })
    }
  } else {
    // 14x14 board for 3-4 players
    const pieceOrder: PieceType[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook']

    // Player 0 (White) - bottom edge
    if (playerCount >= 1) {
      for (let col = 3; col < 11; col++) {
        positions.push({
          col, row: 12,
          piece: { type: 'pawn', owner: 0, facing: 'UP', hasMoved: false }
        })
        positions.push({
          col, row: 13,
          piece: { type: pieceOrder[col - 3], owner: 0, facing: 'UP', hasMoved: false }
        })
      }
    }

    // Player 1 (Red) - top edge
    if (playerCount >= 2) {
      for (let col = 3; col < 11; col++) {
        positions.push({
          col, row: 1,
          piece: { type: 'pawn', owner: 1, facing: 'DOWN', hasMoved: false }
        })
        positions.push({
          col, row: 0,
          piece: { type: pieceOrder[col - 3], owner: 1, facing: 'DOWN', hasMoved: false }
        })
      }
    }

    // Player 2 (Blue) - left edge
    if (playerCount >= 3) {
      for (let row = 3; row < 11; row++) {
        positions.push({
          col: 1, row,
          piece: { type: 'pawn', owner: 2, facing: 'RIGHT', hasMoved: false }
        })
        positions.push({
          col: 0, row,
          piece: { type: pieceOrder[row - 3], owner: 2, facing: 'RIGHT', hasMoved: false }
        })
      }
    }

    // Player 3 (Green) - right edge
    if (playerCount >= 4) {
      for (let row = 3; row < 11; row++) {
        positions.push({
          col: 12, row,
          piece: { type: 'pawn', owner: 3, facing: 'LEFT', hasMoved: false }
        })
        positions.push({
          col: 13, row,
          piece: { type: pieceOrder[row - 3], owner: 3, facing: 'LEFT', hasMoved: false }
        })
      }
    }
  }

  return positions
}

/**
 * Build initial game state
 */
export function buildInitialState(config: GameConfig): GameState {
  const { boardSize, playerCount, mode, eliminationMode } = config
  const lossCondition = config.lossCondition ?? 'king_captured'

  // Create empty board
  const squares = generateValidSquares(boardSize)

  // Create players (apply bot slot configuration if provided)
  const players = createPlayers(playerCount, config.botSlots)

  // Place initial pieces
  const startingPositions = getStartingPositions(boardSize, playerCount)
  for (const { col, row, piece } of startingPositions) {
    const key = `${col},${row}`
    if (squares[key]) {
      squares[key].piece = piece
    }
  }

  const pickupPlacement = config.pickupPlacement ?? 'balanced'

  // Seed initial pickups: one per player
  let baseState: GameState = {
    boardSize,
    squares,
    players,
    activePlayer: 0,
    turnNumber: 1,
    phase: 'active',
    eventQueue: config.eventsEnabled ? generateInitialEvents(1, config.eventCadence ?? EVENT_CADENCE) : [],
    eventCadence: config.eventCadence ?? EVENT_CADENCE,
    moveHistory: [],
    pendingMoves: [],
    movedThisRound: [],
    mode,
    eliminationMode,
    lossCondition,
    winner: null,
    capturedBy: {},
    pickups: {},
    pickupPlacement,
    pickupLifetimeTurns: config.pickupLifetimeTurns ?? 5,
    instantaneousWindowMs: 5000,
    windowOpenAt: null
  }

  const spawn = pickupPlacement === 'balanced' ? spawnPickupBalanced : spawnPickup
  for (let i = 0; i < playerCount; i++) {
    baseState = spawn(baseState)
  }

  return baseState
}

/**
 * Test helper: place a piece on the board (returns new state)
 */
export function placePiece(state: GameState, col: number, row: number, piece: Piece): GameState {
  const key = `${col},${row}`
  if (!state.squares[key]) {
    throw new Error(`Invalid square: ${key}`)
  }

  return {
    ...state,
    squares: {
      ...state.squares,
      [key]: {
        ...state.squares[key],
        piece
      }
    }
  }
}

/**
 * Test helper: build an empty board with no pieces
 */
export function buildEmptyState(config: { boardSize: 8 | 14, players: number }): GameState {
  const squares = generateValidSquares(config.boardSize)
  const players = createPlayers(config.players)

  return {
    boardSize: config.boardSize,
    squares,
    players,
    activePlayer: 0,
    turnNumber: 1,
    phase: 'active',
    eventQueue: [],
    moveHistory: [],
    pendingMoves: [],
    movedThisRound: [],
    mode: 'sequential',
    eliminationMode: 'last_standing',
    lossCondition: 'king_captured',
    winner: null,
    capturedBy: {},
    pickups: {},
    pickupPlacement: 'balanced',
    pickupLifetimeTurns: 5,
    instantaneousWindowMs: 5000,
    windowOpenAt: null,
    eventCadence: EVENT_CADENCE,
  }
}